<template>
    <div class="PRPagination">
        <template v-if="showPagination">
            <div :class="buttonClass">
                <nav-arrow
                    direction="left"
                    :num-size="numSize"
                    :size="size"
                    :disabled="paginationData.currentPage === 1"
                    @page-left="navToPage(paginationData.currentPage - 1)"
                />

                <template v-if="paginationData.totalPages < 8">
                    <NavButton
                        v-for="page in paginationData.totalPages"
                        :key="page"
                        :size="size"
                        :display-number="page"
                        :is-active="paginationData.currentPage === page"
                        @page="p => navToPage(p)"
                    />
                </template>
                <template v-else>
                    <NavButton
                        v-for="page in initialButtonSet"
                        :key="page"
                        :size="size"
                        :display-number="page"
                        :is-active="paginationData.currentPage === page"
                        @page="p => navToPage(p)"
                    />

                    <template v-if="middleButtonSet.length > 0">
                        <span
                            v-if="showInitialEllipsis"
                            :class="['PRPagination__ellipsis', `PRPagination__ellipsis--${size}`]"
                        >. . .</span>
                        <NavButton
                            v-for="page in middleButtonSet"
                            :key="page"
                            :size="size"
                            :display-number="page"
                            :is-active="paginationData.currentPage === page"
                            @page="p => navToPage(p)"
                        />
                        <span
                            v-if="showFinalEllipsis"
                            :class="['PRPagination__ellipsis', `PRPagination__ellipsis--${size}`]"
                        >. . .</span>
                    </template>
                    <span
                        v-else
                        :class="['PRPagination__ellipsis', `PRPagination__ellipsis--${size}`]"
                    >. . .</span>

                    <NavButton
                        v-for="page in finalButtonSet"
                        :key="page"
                        :size="size"
                        :display-number="page"
                        :is-active="paginationData.currentPage === page"
                        @page="p => navToPage(p)"
                    />
                </template>

                <nav-arrow
                    direction="right"
                    :num-size="numSize"
                    :size="size"
                    :disabled="paginationData.currentPage === paginationData.totalPages"
                    @page-right="navToPage(paginationData.currentPage + 1)"
                />
            </div>
            <small
                v-if="showItems"
                :class="totalClass"
            >
                <template v-if="!isLoading">Showing {{ items.count }} of {{ items.total }}</template>
            </small>
        </template>
    </div>
</template>
<script>
import has from 'lodash/has';
import NavArrow from './components/_navArrows';
import NavButton from './components/_navButton';
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';

export default {
    name: 'PRPagination',
    components: {
        NavArrow,
        NavButton
    },
    props: {
        pagination: {
            type: Object,
            default: () => ({
                pagination: {
                    totalPages: -1,
                    currentPage: -1
                }
            }),
            validator: (obj) => {
                return has(obj.pagination, 'totalPages')
                    && has(obj.pagination, 'currentPage');
            }
        },
        size: {
            type: String,
            default: 'sm',
            validator: (val) => ['sm', 'lg'].indexOf(val) !== -1,
        },
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    computed: {
        numSize(){
            switch(this.size){
            case 'lg':
                return 16;
            default:
                return 8;
            }
        },
        paginationData(){
            return this.pagination.pagination;
        },
        showItems(){
            return has(this.pagination, 'items');
        },
        items(){
            return this. showItems
                ? this.pagination.items
                : {
                    total: -1,
                    start: -1,
                    end: -1,
                    name: '',
                    count: -1
                };
        },
        showPagination(){
            return this.paginationData.totalPages > 1;
        },
        isLoading(){
            return this.status === stat.LOADING;
        },
        buttonClass(){
            return statusBuilder('PRPagination__button-row', this.status);
        },
        totalClass(){
            return statusBuilder('PRPagination__total-row', this.status);
        },
        initialButtonSet(){
            return [1, 2, 3];
        },
        finalButtonSet(){
            return [this.paginationData.totalPages - 2, this.paginationData.totalPages - 1, this.paginationData.totalPages];
        },
        showInitialEllipsis(){
            return this.paginationData.currentPage - 1 > 4;
        },
        showFinalEllipsis(){
            return this.paginationData.currentPage + 1 < this.paginationData.totalPages - 3;
        },
        middleButtonSet(){
            return [this.paginationData.currentPage - 1, this.paginationData.currentPage, this.paginationData.currentPage + 1]
                .filter(x =>  x > 0 && !this.initialButtonSet.includes(x) && !this.finalButtonSet.includes(x));
        },
    },
    methods: {
        navToPage(page){
            this.$emit('nav-to-page', page);
        }
    }
};
</script>
<style lang="scss">
.PRPagination {
    display:flex;
    flex-direction: column;
    align-items: center;

    &__button-row,
    &__total-row {
        display: flex;
        margin-bottom: 1rem;
        align-items: center;
    }
    &__button-row {
        @include loading(2rem, 15rem)
    }
    &__total-row {
        @include loading(11px, 5.5rem)
    }
    &__ellipsis {
        color: $text-grey;

        &--sm {
            font-size: 1rem;
            margin: 0 .25rem;
        }
        &--lg {
            font-size: 1rem;
            margin: 0 .5rem;
        }
    }
}
.NavArrow {
    outline: none;
    cursor: pointer;
    border-radius: 4px;
    border-width: 0;
    background: $text-link;
    padding: 0;
    display: flex;

    &--sm {
        margin: 0 .25rem;
        height: 2rem;
        width: 2rem;
    }
    &--lg {
        margin: 0 .5rem;
        height: 2.5rem;
        width: 2.5rem;
    }


    &--disabled {
        cursor: default;
        background: $grey-200
    }
    &__arrow {
        margin: auto;
        &--left { transform: rotate(90deg); }
        &--right { transform: rotate(-90deg); }
    }
}
.NavButton {
    background: $white;
    cursor: pointer;
    padding: 0;
    border: 1px solid $grey-200;
    box-sizing: border-box;
    border-radius: 4px;
    text-align: center;
    color: $text-grey;

    &--sm {
        font-size: 1rem;
        margin: 0 .25rem;
        height: 2rem;
        width: 2rem;
    }
    &--lg {
        font-size: 1rem;
        margin: 0 .5rem;
        height: 2.5rem;
        width: 2.5rem;
    }

    &:hover {
        border-color: $text-link;
        background: $text-link;
        color: $white;
    }

    &--active {
        border-width: 0;
        background: $white;
        color: $text-link;
        cursor: default;
        &:hover {
            background: $white;
            color: $text-link;
        }
    }
}
</style>
